import { notFoundAction, permanentRedirectAction, renderAction } from '@rea-argonaut/actions';
import { GetAction } from '@property.com.au/argonaut';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { fetchSchoolPageBySlug } from '../graphql/fetchSchoolPageBySlug';
import { fetchSchoolPageBySlugTabContent } from '../graphql/fetchSchoolPageBySlugTabContent';
import { getHead } from './getHead';
import { trackSchoolPageView } from '../../analytics/helpers/trackSchoolPageView';
import { SchoolPageScreen } from '../screens/SchoolPageScreen';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { Provider as UrqlProvider } from 'urql';
import { SchoolIdContext } from '../../contexts/SchoolIdContext/SchoolIdContext';
import { ModalContextProvider } from '../../contexts/ModalContext/ModalContext';
import { allowIndexing } from '../helpers/allowIndexing';

export type SchoolPagePathParams = {
  state: 'vic' | 'nsw' | 'nt' | 'qld' | 'sa' | 'tas' | 'wa' | 'act';
  suburbAndPostcode: string;
  schoolName: string;
  schoolId: string;
  childPath?: string;
};

export const getAction: GetAction<SchoolPagePathParams> = async ({
  url,
  user,
  pathParams: { schoolId, childPath },
}) => {
  const urqlClient = await createUrqlClient();
  const schoolPageBySlugPromise = await fetchSchoolPageBySlug({
    client: urqlClient,
    pathname: url.pathname,
    schoolId,
    childPath,
  });
  const { data, error } = schoolPageBySlugPromise;

  if (error?.networkError) {
    throw new Error('Could not successfully connect to Experience API');
  }

  if (data?.redirectSchoolSlugFromSchoolSlug) {
    return permanentRedirectAction(`/${data?.redirectSchoolSlugFromSchoolSlug}`);
  }

  if (!data?.schoolPageBySlug) {
    return notFoundAction();
  }

  //Mimic the current implementation of loading TabContent only when we have the main content resolved
  //Space for improvement here - will revisit, but need to unblock future work first
  const schoolPageTabContentPromise = await fetchSchoolPageBySlugTabContent({
    client: urqlClient,
    pathname: url.pathname,
    schoolId,
    childPath,
  });

  const { data: tabContentData } = schoolPageTabContentPromise;
  if (!tabContentData?.schoolPageBySlug?.tabContent) {
    return notFoundAction();
  }

  await writeUrqlCacheToExchange();

  const helmet = tabContentData?.schoolPageBySlug?.tabContent?.helmet || null;
  if (data.schoolPageBySlug?.tracking) {
    trackSchoolPageView({
      tracking: data.schoolPageBySlug?.tracking,
      user,
    });
  }

  const indexing = allowIndexing(tabContentData?.schoolPageBySlug?.tabContent);

  return renderAction(async () => ({
    getElements: async () => {
      return {
        Screen: (
          <UrqlProvider value={urqlClient}>
            <UrlContextProvider url={url} childPath={childPath}>
              <SchoolIdContext.Provider value={schoolId}>
                <ModalContextProvider>
                  <SchoolPageScreen childPath={childPath} />
                </ModalContextProvider>
              </SchoolIdContext.Provider>
            </UrlContextProvider>
          </UrqlProvider>
        ),
      };
    },
    getHead: async () => (helmet ? getHead(helmet, indexing) : ''),
  }));
};
